import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import AnimatedSection from '../components/AnimatedSection';
import SEO from '../components/SEO';
import { FaUserCheck, FaWhatsapp, FaWpforms, FaFileUpload, FaFileAlt } from 'react-icons/fa';
import './SpmbPage.css';

// Import Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Import Lightbox
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";

const SpmbPage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({ spmbWhatsappNumbers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const fetchSpmbContent = useCallback(async () => {
    try {
      const spmbDocRef = doc(db, "pageContent", "spmbPage");
      const settingsDocRef = doc(db, "settings", "siteConfig");

      const [spmbDocSnap, settingsDocSnap] = await Promise.all([
        getDoc(spmbDocRef),
        getDoc(settingsDocRef)
      ]);

      const docSnap = spmbDocSnap; // Alias for consistency

      if (docSnap.exists()) {
        const data = docSnap.data();
        setContent({
          ...data,
          spmbPosters: Array.isArray(data.spmbPosters) ? data.spmbPosters : []
        });
      } else {
        // Set default content if nothing is in the database
        setContent({
          introTitle: 'Sistem Penerimaan Murid Baru (SPMB)',
          introDescription: 'Selamat datang di halaman pendaftaran siswa baru SMK Pariwisata Sumbangsih. Kami membuka kesempatan bagi para calon siswa untuk bergabung dan menjadi bagian dari keluarga besar kami.',
          spmbPosters: [{ url: 'https://via.placeholder.com/600x800.png?text=Poster+SPMB', id: 'default' }]
        });
      }

      if (settingsDocSnap.exists()) {
        setSettings(settingsDocSnap.data());
      }
    } catch (err) {
      console.error("Error fetching SPMB content: ", err);
      setError("Gagal memuat konten. Silakan coba lagi nanti.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpmbContent();
  }, [fetchSpmbContent]);

  const openLightbox = (index) => {
    setImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO 
        title="SPMB Online - SMK Pariwisata Sumbangsih" 
        description="Informasi Pendaftaran Peserta Didik Baru (SPMB) SMK Pariwisata Sumbangsih. Syarat, alur pendaftaran, dan formulir online." 
      />
      <PageHeader
        title="Sistem Penerimaan Murid Baru"
        subtitle="Bergabunglah dengan kami dan raih masa depan cerah di dunia pariwisata."
      />

      <AnimatedSection>
        <section className="section">
          <div className="container">
            {loading ? (
              <div className="spmb-intro-skeleton">
                <div className="skeleton-text-container">
                  <div className="skeleton skeleton-title"></div>
                  <div className="skeleton skeleton-line"></div>
                  <div className="skeleton skeleton-line"></div>
                  <div className="skeleton skeleton-line half"></div>
                </div>
                <div className="skeleton skeleton-image"></div>
              </div>
            ) : error ? (
              <p className="error-message">{error}</p>
            ) : content && (
              <div className="spmb-intro-grid">
                <div className="spmb-intro-content">
                  <h2>{content.introTitle}</h2>
                  <p>{content.introDescription}</p>
                  {content.brochureLink && (
                    <div className="spmb-brochure-button-container">
                      <a href={content.brochureLink} className="btn-hero" target="_blank" rel="noopener noreferrer">
                        <FaFileAlt /> Lihat Brosur
                      </a>
                    </div>
                  )}
                </div>
                <div className="spmb-intro-image-container">
                  {content.spmbPosters && content.spmbPosters.length > 1 ? (
                    <Swiper
                      modules={[Navigation, Pagination, Autoplay, A11y]}
                      spaceBetween={0}
                      slidesPerView={1}
                      navigation
                      pagination={{ clickable: true }}
                      loop={true}
                      autoplay={{ delay: 5000, disableOnInteraction: false }}
                      className="spmb-poster-slider"
                    >
                      {content.spmbPosters.map((poster, index) => (
                        <SwiperSlide key={poster.id} onClick={() => openLightbox(index)}>
                          <img src={poster.url} alt={content.introTitle || 'Poster SPMB'} className="spmb-intro-image" />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  ) : content.spmbPosters && content.spmbPosters.length === 1 ? (
                    <div className="spmb-single-poster" onClick={() => openLightbox(0)}>
                      <img src={content.spmbPosters[0].url} alt={content.introTitle || 'Poster SPMB'} className="spmb-intro-image" />
                    </div>
                  ) : (
                    <p>Tidak ada poster tersedia.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </AnimatedSection>

      {/* Placeholder for other sections */}
      <AnimatedSection>
        <section className="section section-alt">
          <div className="container">
            <h2>Alur Pendaftaran</h2>
            <div className="spmb-flow-grid">
              <div className="flow-step-card">
                <FaWpforms className="flow-step-icon" />
                <h3>1. Isi Formulir Online</h3>
                <p>Calon siswa mengisi formulir pendaftaran secara lengkap melalui link yang tersedia.</p>
              </div>
              <div className="flow-step-card">
                <FaFileUpload className="flow-step-icon" />
                <h3>2. Siapkan Berkas</h3>
                <p>Melengkapi dan menyiapkan semua dokumen persyaratan yang dibutuhkan untuk dibawa saat daftar ulang.</p>
              </div>
              <div className="flow-step-card">
                <FaUserCheck className="flow-step-icon" />
                <h3>3. Pendaftaran Selesai</h3>
                <p>Pendaftaran berhasil. Informasi selanjutnya akan diumumkan melalui kontak yang terdaftar.</p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* --- Bagian CTA (Daftar Sekarang) --- */}
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <div className="spmb-cta-container">
              <h2>Siap Bergabung?</h2>
              <p>Jangan lewatkan kesempatan untuk menjadi bagian dari sekolah pariwisata terbaik. Daftar sekarang atau hubungi kami untuk informasi lebih lanjut.</p>
              <div className="cta-buttons">
                <Link to={settings.spmbRegistrationLink || "#"} className="btn-hero" target={settings.spmbRegistrationLink ? "_blank" : "_self"} rel="noopener noreferrer">
                  Daftar Sekarang
                </Link>
                {(settings.spmbWhatsappNumbers || []).map((contact, index) => (
                  <a 
                    key={index}
                    href={`https://wa.me/${contact.number.replace(/\D/g, '')}`} 
                    className="btn-whatsapp" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    <FaWhatsapp /> Hubungi {contact.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <Lightbox 
        open={lightboxOpen} 
        close={() => setLightboxOpen(false)} 
        slides={content?.spmbPosters?.map(poster => ({ src: poster.url, title: content.introTitle })) || []} 
        index={imageIndex} 
        plugins={[Captions, Zoom]}
        click={(api) => api.toggleControls()}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 5,
          click: 0, // Menonaktifkan zoom saat klik tunggal
        }}
        animation={{
          zoom: 500,
          swipe: 250,
        }}
      />
    </>
  );
};

export default SpmbPage;